
import React from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import LoadingIndicator from '../../common/LoadingIndicator';
import { useResponsiveCharts } from '@/hooks/useResponsiveCharts';

export interface BaseChartProps {
  title: string;
  description: string;
  data: any[];
  loading: boolean;
  exportCSV: (data: any[], filename: string) => void;
  exportFilename: string;
  children: React.ReactNode;
  height?: number;
  className?: string;
  chartType?: 'bar' | 'pie' | 'line';
}

const BaseChart = ({
  title,
  description,
  data,
  loading,
  exportCSV,
  exportFilename,
  children,
  height: fixedHeight,
  className,
  chartType = 'bar',
}: BaseChartProps) => {
  const { height: responsiveHeight, containerClassName, shouldScroll } = useResponsiveCharts(
    data.length, 
    chartType
  );

  // Use fixed height if provided, otherwise use responsive height
  const chartHeight = fixedHeight || responsiveHeight;

  return (
    <div className="space-y-4 w-full">
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => exportCSV(data, exportFilename)}
          disabled={data.length === 0 || loading}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>
      
      <Card className="w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg md:text-xl">{title}</CardTitle>
          <CardDescription className="text-sm">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-2 md:p-6">
          <div 
            style={{ 
              height: `${chartHeight}px`,
              touchAction: shouldScroll ? 'pan-x' : 'auto'
            }} 
            className={`w-full ${containerClassName} ${shouldScroll ? 'overflow-x-auto' : ''}`}
          >
            {loading ? (
              <LoadingIndicator message={`Loading ${title.toLowerCase()} data...`} />
            ) : (
              data.length > 0 ? (
                <div className="w-full h-full">
                  {children}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground text-center px-4">No {title.toLowerCase()} data available for the selected period</p>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BaseChart;
